import "react-circular-progressbar/dist/styles.css";

import CreateModeButton from "./CreateModeButton";
import SketchDialog from "../sketch/SketchDialog";
import { useRef, useState } from "react";
import { PaintBrushIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Transition } from "@headlessui/react";
import CreateButton from "../CreateButton";

export function PaperClipIcon({ className }) {
    return (
        <svg
            className={className}
            version="1.1"
            viewBox="-5 -13 75.000877 74.806664"
            fill="currentColor"
        >
            <path
                d="m 24.168,61.806566 c -7.7891,0 -15.113,-3.0352 -20.625,-8.5469 -5.5117,-5.5078 -8.543,-12.828 -8.543,-20.621 0,-7.7891 3.0312,-15.113 8.543,-20.625 L 24.75,-7.0413336 c 7.9531,-7.9688004 21.176,-7.9727004 29.301,0.15625 8.1211,8.1211 8.1211,21.3399996 0,29.4649996 l -21.207,19.051 c -2.1953,2.2031 -5.3359,3.5078 -8.6758,3.5078 -3.3398,0 -6.4766,-1.3008 -8.8398,-3.6602 -2.3594,-2.3594 -3.6602,-5.5 -3.6602,-8.8398 0,-3.3359 1.3008,-6.4766 3.6602,-8.8398 L 36.5314,4.7479164 c 1.707,-1.5352 4.3438,-1.4023 5.8867,0.31641 1.5391,1.7148 1.3945,4.3477 -0.31641,5.8866996 l -21.043,18.898 c -1.4648,1.4688 -1.4648,4.1133 0.16016,5.7383 1.5742,1.5781 4.3203,1.5781 5.8906,0 l 21.211,-19.051 c 4.707,-4.7188 4.707,-12.6519996 -0.16406,-17.52299957 -4.8789,-4.87110003 -12.809,-4.87890003 -17.68,0 L 9.27339,18.064326 c -3.7734,3.7812 -5.9375,9.0117 -5.9375,14.578 0,5.5625 2.168,10.797 6.1016,14.73 3.9336,3.933 9.168,6.1055 14.73,6.1055 5.5664,0 10.797,-2.168 14.73,-6.1055 l 24.133,-21.98 c 1.6992,-1.5508 4.332,-1.4258 5.8828,0.27344 1.5508,1.7031 1.4297,4.3359 -0.27734,5.8867 l -23.992,21.844 c -5.3633,5.375 -12.688,8.4102 -20.477,8.4102 z"
                id="path2"
            />
        </svg>
    );
}

function SketchButton({
    sketchState,
    setSketchOpen,
    setSketchState,
    setSketchBinaryData,
}) {
    return sketchState ? (
        <span className="isolate inline-flex rounded-md shadow-sm text-secondary dark:text-secondary-light">
            <button
                onClick={(e) => {
                    setSketchOpen(true);
                    e.preventDefault();
                    window.umami?.track("Create Prompt - Sketch", {});
                }}
                type="button"
                className="relative inline-flex items-center gap-x-1.5 rounded-l-md bg-white dark:bg-gray-700 dark:hover:bg-gray-600 dark:ring-gray-600 px-3 pr-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
            >
                <PaintBrushIcon
                    className="-ml-0.5 h-4 w-4 text-secondary dark:text-secondary-light mr-1"
                    aria-hidden="true"
                />
                Sketch&nbsp;
            </button>
            <button
                onClick={(e) => {
                    setSketchState(null);
                    setSketchBinaryData(null);
                    e.preventDefault();
                    window.umami?.track("Create Prompt - Sketch - Delete", {});
                }}
                type="button"
                className="relative -ml-px inline-flex items-center rounded-r-md bg-white dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-600 px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 focus:z-10"
            >
                <TrashIcon className="w-4 h-4 dark:fill-secondary-light" />
            </button>
        </span>
    ) : (
        <button
            type="button"
            className="relative inline-flex items-center rounded-md bg-white dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 dark:ring-gray-600 px-3 py-2 text-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 focus:z-10"
            onClick={(e) => {
                setSketchOpen(true);
                e.preventDefault();
                window.umami?.track("Create Prompt - Sketch - Open", {});
            }}
        >
            <PaintBrushIcon className="-ml-0.5 w-4 h-4 mr-2" />
            <div>Sketch</div>
        </button>
    );
}

function UploadButton({
    uploadState,
    setUploadState,
    setUploadBinaryData,
}) {
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setUploadBinaryData(e.target.result);
                setUploadState(true);
            };
            reader.readAsArrayBuffer(file);
        }
    };

    return uploadState ? (
        <span className="isolate inline-flex rounded-md shadow-sm text-secondary dark:text-secondary-light">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".png,.jpg,.jpeg,.webp"
                className="hidden"
            />
            <button
                onClick={() => fileInputRef.current?.click()}
                type="button"
                className="relative inline-flex items-center gap-x-1.5 rounded-l-md bg-white dark:bg-gray-700 dark:hover:bg-gray-600 dark:ring-gray-600 px-3 pr-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
            >
                <PaperClipIcon
                    className="-ml-0.5 h-4 w-4 text-secondary dark:text-secondary-light mr-1"
                    aria-hidden="true"
                />
                Attach&nbsp;
            </button>
            <button
                onClick={(e) => {
                    setUploadState(null);
                    setUploadBinaryData(null);
                    e.preventDefault();
                    window.umami?.track("Create Prompt - Upload - Delete", {});
                }}
                type="button"
                className="relative -ml-px inline-flex items-center rounded-r-md bg-white dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-600 px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 focus:z-10"
            >
                <TrashIcon className="w-4 h-4 dark:fill-secondary-light" />
            </button>
        </span>
    ) : (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".png,.jpg,.jpeg,.webp"
                className="hidden"
            />
            <button
                type="button"
                className="relative inline-flex items-center rounded-md bg-white dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 dark:ring-gray-600 px-3 py-2 text-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 focus:z-10"
                onClick={() => fileInputRef.current?.click()}
            >
                <PaperClipIcon className="-ml-0.5 w-4 h-4 mr-2" />
                <div>Attach</div>
            </button>
        </>
    );
}

export default function CreatePrompt(props) {
    const {
        isCreating,
        setPrompt,
        setSelectedMode,
        onCreate,
        onBulkCreate,
        prompt,
        setSketchBinaryData,
        setUploadBinaryData,
        creditsRemaining,
        showPremium,
        hideMisc,
        placeholder,
        isSimple,
    } = props;
    const [sketchOpen, setSketchOpen] = useState(false);
    const [sketchState, setSketchState] = useState(false);
    const [uploadState, setUploadState] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltip, setTooltip] = useState(
        "Private - Only you can see the created image"
    );

    return (
        <div
            className={
                props.className ||
                "flex space-x-4 items-center justify-center align-bottom sm:mx-32 md:mx-48 lg:mx-48 2xl:mx-96 mx-4 my-2 sm:my-8 mb-2 "
            }
        >
            <div
                className={
                    "bg-white min-w-0 flex-1  dark:shadow-none dark:bg-gray-800 rounded-lg align-end self-end " +
                    (props.className ? "shadow-none" : "shadow-xl shadow-slate-200")
                }
            >
                <form action="#" className="relative">
                    <div className="overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 shadow-sm">
                        <label htmlFor="comment" className="sr-only">
                            Add
                        </label>
                        <textarea
                            autoFocus={true}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={1}
                            name="comment"
                            id="comment"
                            className="block w-full resize-none border-0 p-6 mb-8 text-lg outline-none dark:bg-gray-800 dark:text-white"
                            placeholder={placeholder || "What do you want to see?"}
                            defaultValue={prompt}
                        />

                        {/* Spacer element to match the height of the toolbar */}
                        <div className="py-2" aria-hidden="true">
                            {/* Matches height of button in toolbar (1px border + 36px content height) */}
                            <div className="py-px">
                                <div className="h-9" />
                            </div>
                        </div>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 flex justify-between py-4 pl-2 mx-2 pr-2 lg:mx-4 sm:items-center sm:border-t border-t-gray-100 dark:border-t-gray-700 flex-col sm:flex-row">
                        {isSimple ? <div className="flex-1"> </div> : <div className="flex items-center">
                            <CreateModeButton
                                initialMode={props.initialMode}
                                initialAuxMode={props.initialAuxMode}
                                onSetMode={(mode, auxMode) =>
                                    setSelectedMode(mode, auxMode)
                                }
                            />
                            {!hideMisc && (
                                <div
                                    className="ml-4 hidden lg:flex"
                                    title="Attach a sketch to guide the AI"
                                >
                                    <SketchButton
                                        sketchState={sketchState}
                                        setSketchOpen={setSketchOpen}
                                        setSketchState={setSketchState}
                                        setSketchBinaryData={
                                            setSketchBinaryData
                                        }
                                    />
                                </div>
                            )}
                            {!hideMisc && (
                                <div
                                    className="ml-4 hidden lg:flex"
                                    title="Attach an image"
                                >
                                    <UploadButton
                                        uploadState={uploadState}
                                        setUploadState={setUploadState}
                                        setUploadBinaryData={
                                            setUploadBinaryData
                                        }
                                    />
                                </div>
                            )}
                            <div
                                className="relative"
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="hidden sm:block w-5 h-5 fill-gray-500 ml-4"
                                >
                                    <path d="M3.53 2.47a.75.75 0 00-1.06 1.06l18 18a.75.75 0 101.06-1.06l-18-18zM22.676 12.553a11.249 11.249 0 01-2.631 4.31l-3.099-3.099a5.25 5.25 0 00-6.71-6.71L7.759 4.577a11.217 11.217 0 014.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113z" />
                                    <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0115.75 12zM12.53 15.713l-4.243-4.244a3.75 3.75 0 004.243 4.243z" />
                                    <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 00-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 016.75 12z" />
                                </svg>
                                <Transition.Root
                                    style={{
                                        left: -100,
                                        top: -40,
                                    }}
                                    className="hidden md:block absolute pointer-events-none top-8"
                                    show={showTooltip}
                                >
                                    <Transition.Child
                                        className={
                                            "whitespace-nowrap backdrop-blur-sm bg-black bg-opacity-70 text-white font-medium text-xs rounded-lg px-4 p-1"
                                        }
                                        enter="transition-all ease-in-out duration-200"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="transition-all ease-in-out duration-200"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        {tooltip}
                                    </Transition.Child>
                                </Transition.Root>
                            </div>
                        </div>}
                        {(creditsRemaining || creditsRemaining === 0) && (
                            <div
                                className={
                                    "hidden sm:flex justify-end flex-1 text-right text-xs mr-4 hover:underline cursor-pointer " +
                                    (creditsRemaining <= 0
                                        ? "text-red-600"
                                        : "text-gray-500")
                                }
                                onClick={() => {
                                    showPremium?.();
                                    window.umami?.track(
                                        "Create Prompt - Explicit Show premium",
                                        {}
                                    );
                                }}
                            >
                                {/* <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="h-3 w-3"
                                >
                                    <circle cx="8" cy="8" r="6"></circle>
                                    <path d="M18.09 10.37A6 6 0 1 1 10.34 18"></path>
                                    <path d="M7 6h1v4"></path>
                                    <path d="m16.71 13.88.7.71-2.82 2.82"></path>
                                </svg> */}
                                <span>{creditsRemaining}</span>&nbsp;
                                <span>
                                    Credit{creditsRemaining === 1 ? "" : "s"}{" "}
                                    <span className="sm:inline hidden">
                                        left
                                    </span>
                                </span>
                            </div>
                        )}
                        <div className="flex-shrink-0 flex items-center mt-2 sm:mt-0">
                            <CreateButton
                                isCreating={isCreating}
                                hideMenu={isSimple}
                                onCreate={onCreate}
                                onBulkCreate={onBulkCreate}
                                hasUpload={uploadState}
                                hasSketch={sketchState}
                                onUpload={(data) => {
                                    if (data) {
                                        setUploadBinaryData(data);
                                        setUploadState(true);
                                    }
                                }}
                                onSketch={() => setSketchOpen(true)}
                            />
                        </div>
                    </div>
                </form>
            </div>
            {sketchOpen && (
                <SketchDialog
                    sketchState={sketchState}
                    onSave={(data) => {
                        setSketchBinaryData(data.buffer);
                        setSketchState(data.state);
                    }}
                    onClose={() => setSketchOpen(false)}
                />
            )}
        </div>
    );
}
